import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrderDetails } from '../services/api';
import PrintableOrder from './PrintableOrder';
import '../styles/InfiniteTable.css';
import './OrderDetail.css';

function OrderDetail() {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { orderId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrderDetail();
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getOrderDetails(orderId);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order detail:', error);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" size="lg">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading order details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {error}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/orders')}>
          Back to Orders
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          Order not found.
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/orders')}>
          Back to Orders
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Order #{order.idTrans}</h2>
        <Button variant="secondary" onClick={() => navigate('/orders')}>
          Back to Orders
        </Button>
      </div>

      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>Order Information</Card.Header>
            <Card.Body>
              <p><strong>Order Number:</strong> {order.idTrans}</p>
              <p><strong>Date:</strong> {new Date(order.transDate).toLocaleDateString()}</p>
              <p><strong>Type:</strong> {order.typeName}</p>
              <p><strong>Customer Reference:</strong> {order.CustomerRef || 'N/A'}</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>Shipping Information</Card.Header>
            <Card.Body>
              <p><strong>Ship To:</strong> {order.delName || 'N/A'}</p>
              <p><strong>Carrier:</strong> {order.carrier || 'N/A'}</p>
              <p><strong>Tracking:</strong> {order.carrierPro || 'N/A'}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>Order Items</Card.Header>
        <Card.Body style={{ padding: 0 }}>
          <div className="table-wrapper" style={{ height: 'calc(100vh - 500px)' }}>
            <div className="table-scroll">
              <Table className="infinite-table">
                <thead>
                  <tr>
                    <th data-align="right">Lot ID</th>
                    <th data-align="left">Item Number</th>
                    <th data-align="left">Description</th>
                    <th data-align="left">Notes</th>
                    <th data-align="right">PPU</th>
                    <th data-align="right">Total Pieces</th>
                    <th data-align="right">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {order?.items?.map((item) => {
                    const ppu = item.ppu || 1;
                    const quantity = item.transQty || 0;
                    const totalPieces = ppu * quantity;
                    
                    return (
                    <tr key={item.idTrans2}>
                      <td style={{ textAlign: 'right', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                        {item.recTrans || 'N/A'}
                      </td>
                      <td style={{ textAlign: 'left', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                        <strong>{item.itemNumber || 'N/A'}</strong>
                      </td>
                      <td style={{ textAlign: 'left', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                        {item.itemName || 'N/A'}
                      </td>
                      <td style={{ textAlign: 'left', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                        {item.levelNotes || item.notes || ''}
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                        {ppu}
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                        <strong>{totalPieces.toLocaleString()}</strong>
                      </td>
                      <td style={{ textAlign: 'right', padding: '6px 12px', fontSize: '0.85rem', lineHeight: '1.2' }}>
                        {quantity}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
              {(!order?.items || order.items.length === 0) && (
                <div className="table-empty">
                  No items found for this order.
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      <PrintableOrder order={order} />
    </Container>
  );
}

export default OrderDetail;
